import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router";
import { useSelector, useDispatch } from "react-redux";
import { Modal } from "antd";
import { RequestCard } from "./requestCard";
import { ClockCircleOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { createFeatureRequest } from "api/featureRequests/createFeatureRequest";
import { getFeatureRequest } from "api/featureRequests/getFeatureRequest";
import { buildAdminMenu } from "constants/adminMenuConfig";
import { setAdminMobileSidebarStatus, setFeatureRequest } from "actions/user";
import { eventTrack } from "helpers/ga4Helper";
import { computeFinalPermissions } from "helpers/finalPermissions";
import { Drawer } from "antd";
import menuClose from "assets/images/logo/Vector.png";

const Sidebar = () => {
  const dispatch = useDispatch();
  const userInfoRedux = useSelector((s) => s.user.userInfo);
  const usersPermissionRedux = useSelector((s) => s.user.userPermission);
  const companyPermissionRedux = useSelector((s) => s.user.companyPermission);
  const allPermissionRedux = useSelector((s) => s.user.allPermission);
  const FinalPermissions = computeFinalPermissions({
    userInfo: userInfoRedux,
    allPermission: allPermissionRedux,
    companyPermission: companyPermissionRedux,
    userPermission: usersPermissionRedux,
  });

  const adminSidebarStatusRedux = useSelector((s) => s.user?.adminSidebar);

  const history = useHistory();
  const location = useLocation();
  const [requestModal, setRequestModal] = useState(false);
  const [requestType, setRequestType] = useState("");
  const [loading, setLoading] = useState(false);
  const [featureRequests, setFeatureRequests] = useState([]);

  const [openSidebar, setOpenSidebar] = useState(false);

  const handlenavigate = (url) => {
    history.push(url);
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await getFeatureRequest(userInfoRedux?.id);
        if (res)
          setFeatureRequests(res.map((feature) => feature.requestedFeature));
      } catch (err) {
        console.log(err);
      }
    })();
  }, []);

  const requestAccess = async () => {
    setLoading(true);
    const res = await createFeatureRequest(userInfoRedux.id, {
      companyId: userInfoRedux.company,
      permission: requestType,
      userId: userInfoRedux.id,
    });
    if (res.status === 200) {
      setLoading(false);
      dispatch(
        setFeatureRequest(res?.map((feature) => feature.requestedFeature))
      );
      setFeatureRequests(res?.map((feature) => feature.requestedFeature));
    }
  };

  const handleRequestType = (requestType) => {
    setRequestType(requestType);
    setRequestModal(true);
  };

  // Both the internal admin scrollable menu (here) and the global drawer/side
  // menu render from the same config (constants/adminMenuConfig.js), grouped
  // into a Super Admin section on top and an Admin section after it, so a user
  // reaches the same set of access buttons from either surface.
  const { superAdmin: superAdminItems, admin: adminItems } = buildAdminMenu({
    userInfo: userInfoRedux,
    finalPermissions: FinalPermissions,
  });

  const renderMenuButton = (item) => {
    if (item.mode === "request") {
      return (
        <button
          key={item.key}
          className={location.pathname == item.path ? "ActiveTab" : null}
          onClick={() => {
            handleRequestType(item.permission);
            eventTrack(
              "Navigate-Admin-Action",
              "Navigate-Admin",
              userInfoRedux?.name,
              "+1",
              false,
              "+1"
            );
          }}
        >
          <i className={`${item.icon} mr-3`}></i>
          {item.label}
          <p
            className="access-btn"
            onClick={(e) => {
              e.stopPropagation();
              handleRequestType(item.permission);
            }}
          >
            {userInfoRedux?.companyAdmin ? "Acquire License" : "Request Access"}
          </p>
        </button>
      );
    }
    return (
      <button
        key={item.key}
        onClick={() => {
          handlenavigate(item.path);
          eventTrack(
            "Navigate-Admin-Action",
            "Navigate-Admin",
            userInfoRedux?.name,
            "+1",
            false,
            "+1"
          );
        }}
        className={location.pathname == item.path ? "ActiveTab" : null}
      >
        <i className={`${item.icon} mr-3`}></i>
        {item.label}
      </button>
    );
  };

  const InnerSidebar = () => {
    return (
      <div className="position-relative">
        <div
          src={menuClose}
          onClick={() => {
            dispatch(setAdminMobileSidebarStatus(false));
          }}
          className="cursor-pointer mobile-show sidebar-close-btn"
        >
          <i className="bi bi-x-circle"></i>
        </div>

        {superAdminItems && (
          <>
            <p className="admin-menu-section-title font-weight-bold mt-2 mb-1">
              Super Admin
            </p>
            {superAdminItems.map(renderMenuButton)}
          </>
        )}

        {adminItems && (
          <>
            <p className="admin-menu-section-title font-weight-bold mt-3 mb-1">
              Admin
            </p>
            {adminItems.map(renderMenuButton)}
          </>
        )}
      </div>
    );
  };

  // The admin settings menu can carry 20+ entries for a super-admin — more
  // than fit on screen. The desktop sidebar is `position: fixed`, so anything
  // past the fold was simply unreachable (no page scroll moves a fixed box).
  // Bound it to the viewport and let it scroll internally so every button
  // stays clickable. The vh-based height resizes with the screen, and we drop
  // the stylesheet's rigid `min-height: 79vh` (which would otherwise win over
  // max-height on short screens and re-introduce the overflow).
  const desktopMenuStyle = {
    minHeight: "auto",
    maxHeight: "calc(100vh - 150px)",
    overflowY: "auto",
    overflowX: "hidden",
  };

  return (
    <>
      <div
        className="profile-sidebar admin-sidebar admin-sidebar-scrollable desktop-show"
        style={desktopMenuStyle}
      >
        <p className="font-weight-bold">Settings</p>
        <InnerSidebar />
      </div>

      <Modal
        title={
          userInfoRedux?.companyAdmin ? "Acquire License" : "Request Access"
        }
        open={requestModal}
        footer={null}
        onCancel={() => setRequestModal(false)}
      >
        <RequestCard
          status={featureRequests?.includes(requestType)}
          loading={loading}
          icon={
            featureRequests?.includes(requestType) ? (
              <ClockCircleOutlined style={{ color: "black" }} />
            ) : (
              <InfoCircleOutlined style={{ color: "black" }} />
            )
          }
          title={
            featureRequests?.includes(requestType)
              ? `Request Already Sent`
              : `Oops, looks like you need to ${
                  userInfoRedux?.companyAdmin
                    ? `Acquire License`
                    : `Request Access`
                }`
          }
          description={
            featureRequests?.includes(requestType)
              ? `Please reach out to your brand Admin`
              : `Elevate your experience with exclusive futures, early releases, and premium features.`
          }
          buttonText={
            userInfoRedux?.companyAdmin ? "Acquire License" : "Request Access"
          }
          buttonHandler={() => requestAccess()}
        />
      </Modal>
      <div className="mobile-show">
        <Drawer
          placement="left"
          closable={false}
          onClose={() => dispatch(setAdminMobileSidebarStatus(false))}
          open={adminSidebarStatusRedux}
          key="left"
          width={320}
        >
          <div className="profile-sidebar admin-sidebar">
            <InnerSidebar />
          </div>
        </Drawer>
      </div>
    </>
  );
};

export default Sidebar;
